import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import Modal from "react-modal";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const Sampling = () => {
  const [ponds, setPonds] = useState([]);
  const [fishTypes, setFishTypes] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pondName, setPondName] = useState('');
  const [fishType, setFishType] = useState(0);
  const [length, setLength] = useState(0);
  const [weight, setWeight] = useState(0);
  const [ws, setWs] = useState(0);
  const [wr, setWr] = useState(0);
  const [tagNumber, setTagNumber] = useState('');
  const [status, setStatus] = useState('Culled');

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchSamplesPage = useCallback(async ({ search, page, limit }) => {
    try {
      return await axiosInstance.get(`/api/FishSampling/GetAllSamples`, {
        params: { search, page, limit },
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return { data: { data: [], total: 0 } };
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
      throw error;
    }
  }, []);

  const {
    rows: samples,
    totalCount,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch,
  } = useServerPaginatedList(fetchSamplesPage);

  useEffect(() => {
    axiosInstance.get(`api/Pond/GetAllPonds`, { params: { search: '', page: 1, limit: 500 } })
      .then((response) => {
        const body = response.data;
        setPonds(Array.isArray(body) ? body : body?.data || []);
      })
      .catch((error) => {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        showErrorToast(error.response?.data?.message || "Failed to fetch ponds");
      });
  }, []);

  useEffect(() => {
    axiosInstance.get(`/api/FishTypes/GetAllFishTypes`, { params: { search: '', page: 1, limit: 500 } })
      .then((response) => {
        const body = response.data;
        setFishTypes(Array.isArray(body) ? body : body?.data || []);
      })
      .catch((error) => {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        showErrorToast(error.response?.data?.message || "Failed to fetch fish types");
      });
  }, []);

  const CalculateWsWr = async () => {
    if (length > 0 && weight > 0 && fishType > 0) {
      try {
        setIsSubmitting(true);
        const response = await axiosInstance.get(`/api/FishTypes/GetFishType`, {
          params: { id: fishType }
        });
        const expression = response.data.Ws;
        if (length && expression) {
          const replacedExpression = expression.replace(/L/g, length);
          const calculatedWs = Function(`return ${replacedExpression}`)();
          setWs(calculatedWs);
          if (calculatedWs > 0) {
            const calculatedWr = parseFloat(weight / calculatedWs).toFixed(2);
            setWr(calculatedWr);
          } else {
            console.error("Invalid Ws value (division by zero).");
          }
        } else {
          console.error("Invalid length or expression.");
        }
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        showErrorToast(error.response?.data?.message || "An error occurred.");
        console.error("Error calculating Ws/Wr:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await axiosInstance.put(`/api/FishSampling/UpdateSample/${editId}`, {
          Length: length,
          Width: weight,
          Ws: ws,
          Wr: wr,
          TagNumber: tagNumber,
          Status: status,
          PondID: pondName,
          FishTypeID: fishType
        });
        showSuccessToast("Sample updated successfully");
      } else {
        await axiosInstance.post(`/api/FishSampling/AddSample`, {
          Length: length,
          Width: weight,
          Ws: ws,
          Wr: wr,
          TagNumber: tagNumber,
          Status: status,
          PondID: pondName,
          FishTypeID: fishType
        });
        showSuccessToast("Sample added successfully");
      }
      await refetch();
      closeModal();
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
      console.error("Error submitting sample:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && samples.length === 0) {
    return <Preloader />;
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item Active"> Sampling </li>
          </ul>
          <h1 className="page-header mb-0"> Sampling</h1>
        </div>
        <div className="ms-auto" />
      </div>
      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            <div className="input-group mb-3">
              <div className="flex-fill position-relative">
                <div className="input-group">
                  <div
                    className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                    style={{ zIndex: 10 }}
                  >
                    <i className="fa fa-search opacity-5"></i>
                  </div>
                  <input
                    type="text"
                    className="form-control px-35px bg-light"
                    placeholder="Search Sample..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="table-responsive mb-3">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th>Pond Name</th>
                    <th>Pond Fish Type </th>
                    <th>Length</th>
                    <th>Weight</th>
                    <th>Ws</th>
                    <th>Wr</th>
                    <th>Tag Number</th>
                    <th>Account</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-5">
                        Loading…
                      </td>
                    </tr>
                  ) : (
                    samples.map((web) => (
                      <tr key={web.FishSamplingID}>
                        <td>{web.PondName}</td>
                        <td>{web.FishTypeName}</td>
                        <td>{web.Length}</td>
                        <td>{web.Weight}</td>
                        <td>{web.Ws}</td>
                        <td>{web.Wr}</td>
                        <td>{web.TagNumber || '-'}</td>
                        <td>{web.FisherName}</td>
                        <td>
                          {web.Status === "Culled" ? (
                            <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Culled
                            </span>
                          ) : (
                            <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                              <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                              Release
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <ListPagination
              currentPage={currentPage}
              totalCount={totalCount}
              entriesPerPage={entriesPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            width: "800px",
            height: "600px",
            margin: "auto",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -20%)",
            padding: "20px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <form onSubmit={handleSubmit} className="fs-13px">
          <div className="d-flex align-items-center mb-3">
            <div>
              <h2> {isEditing ? "Edit Sample" : "Add Sample"}</h2>
            </div>
            <div className="ms-auto">
              <button
                type="submit"
                className="btn btn-success btn-rounded px-4 rounded-pill"
                disabled={isSubmitting}
              >
                <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
                Submit
              </button>
            </div>
          </div>
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                <div className="form-floating mb-25px">
                  <select
                    className="form-control h-50px fs-13px"
                    id="PondName"
                    name="PondName"
                    value={pondName}
                    onChange={(e) => { setPondName(e.target.value); }}
                  >
                    <option value="0">Select Pond</option>
                    {ponds.map((web) => (
                      <option key={web.id} value={web.id}>{web.Name}</option>
                    ))}
                  </select>
                  <label
                    htmlFor="PondName"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Pond &nbsp; <span>*</span>
                  </label>
                </div>

                <div className="form-floating mb-25px">
                  <select
                    className="form-control h-50px fs-13px"
                    id="FishType"
                    name="FishType"
                    value={fishType}
                    onChange={async (e) => { setFishType(e.target.value); await CalculateWsWr(); }}
                  >
                    <option value="0">Select Fish Type</option>
                    {fishTypes.map((web) => (
                      <option key={web.id} value={web.id}>{web.Name}</option>
                    ))}
                  </select>
                  <label
                    htmlFor="FishType"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Fish Type &nbsp; <span>*</span>
                  </label>
                </div>

                <div className="form-floating mb-15px">
                  <input
                    type="number"
                    step={0.01}
                    className="form-control h-45px fs-13px"
                    placeholder="Length"
                    id="Length"
                    name="Length"
                    value={length}
                    onChange={async (e) => { setLength(e.target.value); await CalculateWsWr(); }}
                  />
                  <label
                    htmlFor="Length"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Length
                  </label>
                </div>

                <div className="form-floating mb-15px">
                  <input
                    type="number"
                    step={0.01}
                    className="form-control h-45px fs-13px"
                    placeholder="Weight"
                    id="Weight"
                    name="Weight"
                    value={weight}
                    onChange={async (e) => { setWeight(e.target.value); await CalculateWsWr(); }}
                  />
                  <label
                    htmlFor="Weight"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Weight
                  </label>
                </div>

                <div className="form-floating mb-15px">
                  <input
                    type="number"
                    step={0.01}
                    className="form-control h-45px fs-13px"
                    placeholder="Ws"
                    id="Ws"
                    name="Ws"
                    value={ws}
                    onChange={(e) => { setWs(e.target.value); }}
                  />
                  <label
                    htmlFor="Ws"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Ws
                  </label>
                </div>

                <div className="form-floating mb-15px">
                  <input
                    type="number"
                    step={0.01}
                    className="form-control h-45px fs-13px"
                    placeholder="Wr"
                    id="Wr"
                    name="Wr"
                    value={wr}
                    onChange={(e) => { setWr(e.target.value); }}
                  />
                  <label
                    htmlFor="Wr"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Wr
                  </label>
                </div>

                <div className="form-floating mb-15px">
                  <input
                    type="text"
                    className="form-control h-45px fs-13px"
                    placeholder="Tag Number"
                    id="TagNumber"
                    name="TagNumber"
                    value={tagNumber}
                    onChange={(e) => { setTagNumber(e.target.value); }}
                  />
                  <label
                    htmlFor="TagNumber"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Tag Number
                  </label>
                </div>

                <div className="row">
                  <div className="col-md-12 d-flex align-items-center">
                    <div className="col-md-12 d-flex align-items-center">
                      <div className="form-check mb-15px me-3">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="Status"
                          id="Status"
                          value="Culled"
                          checked={status === "Culled"}
                          onChange={() => { setStatus("Culled"); }}
                        />
                        <label className="form-check-label" htmlFor="isAdmin">
                          Culled
                        </label>
                      </div>
                      <div className="form-check mb-15px">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="Status"
                          id="StatusReleased"
                          value="Released"
                          checked={status === "Released"}
                          onChange={() => { setStatus("Released"); }}
                        />
                        <label className="form-check-label" htmlFor="isUser">
                          Released
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Sampling;
